import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead, FAQSchema } from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Palette, Eye, BookOpen, Award, CheckCircle, ArrowRight, History, Users } from 'lucide-react';

const ClassicalArtTraining = () => {
  const faqs = [
    {
      question: "What is classical art training?",
      answer: "Classical art training, also known as atelier training, is a method of teaching art based on the techniques and approaches used by master painters from the Renaissance through the 19th century. It emphasizes careful observation, accurate drawing, and mastery of traditional painting methods."
    },
    {
      question: "What is an atelier?",
      answer: "An atelier is a studio or workshop where artists train under the guidance of a master artist. The atelier system was the primary method of art education in Europe for centuries. The Coppini Academy follows this tradition of structured, mentor-based instruction."
    },
    {
      question: "Why choose classical training over modern art school?",
      answer: "Classical training focuses on developing concrete, measurable skills in drawing and painting. Students learn time-tested techniques that allow them to accurately represent what they see, providing a solid foundation for any artistic direction."
    },
    {
      question: "How long does classical art training take?",
      answer: "The depth of study varies by student goals. Basic competency in drawing can be developed in months, while mastery of oil painting techniques may take years of dedicated practice. Our programs accommodate both casual learners and serious students."
    }
  ];

  return (
    <main className="min-h-screen bg-white">
      <SEOHead
        title="Classical Art Training | Atelier Art School Texas"
        description="Learn classical drawing and painting techniques at The Coppini Academy in San Antonio. Atelier-style art training based on methods of the old masters. Enroll today!"
        keywords="classical art training, atelier art school Texas, classical drawing, traditional painting techniques, realist art training, academic art San Antonio"
        canonicalPath="/classical-art-training"
      />
      <FAQSchema faqs={faqs} />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 text-white py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-1 bg-cafa-gold/20 text-cafa-gold text-sm font-medium rounded-full mb-6">
              Atelier Training in the Classical Tradition
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6 leading-tight">
              Classical Art Training
            </h1>
            <p className="text-xl text-stone-300 mb-8 leading-relaxed">
              Study the time-tested techniques of the old masters. Our atelier-based curriculum 
              develops foundational skills in drawing and painting through careful observation, 
              structured practice, and personalized instruction.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-cafa-red hover:bg-cafa-red-900 text-white">
                <Link to="/classes/adult">Start Your Training <ArrowRight className="ml-2" size={18} /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <Link to="/about">About Our Academy</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What is Classical Training */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-6">
                The Atelier Method of Art Education
              </h2>
              <p className="text-lg text-stone-600 mb-4 leading-relaxed">
                Classical art training follows the atelier tradition that produced history&apos;s 
                greatest artists. From the Renaissance workshops of Florence to the French 
                academies of the 19th century, this method has proven its effectiveness 
                over centuries.
              </p>
              <p className="text-lg text-stone-600 mb-6 leading-relaxed">
                At The Coppini Academy of Fine Art, we continue this tradition in San Antonio, 
                Texas. Our curriculum emphasizes careful observation, accurate drawing, 
                understanding of light and form, and mastery of oil painting techniques.
              </p>
              <p className="text-lg text-stone-600 leading-relaxed">
                Unlike modern art programs that may prioritize concept over craft, classical 
                training develops concrete, measurable skills. Students learn to draw and 
                paint with accuracy and sensitivity, building a foundation that supports 
                any artistic direction.
              </p>
            </div>
            <div className="bg-stone-100 rounded-lg p-8">
              <h3 className="text-xl font-semibold text-stone-900 mb-6">Core Principles</h3>
              <div className="space-y-4">
                {[
                  { icon: Eye, title: "Observation", desc: "Training the eye to see accurately" },
                  { icon: BookOpen, title: "Structured Learning", desc: "Progressive skill development" },
                  { icon: History, title: "Traditional Methods", desc: "Time-tested techniques" },
                  { icon: Users, title: "Mentor Guidance", desc: "Personal instruction from professionals" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start">
                    <item.icon className="text-cafa-red mr-3 mt-1 flex-shrink-0" size={24} />
                    <div>
                      <p className="font-medium text-stone-900">{item.title}</p>
                      <p className="text-stone-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Classical Curriculum */}
      <section className="py-16 lg:py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4">
              The Classical Art Curriculum
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Our training follows a logical progression, building skills systematically 
              from basic to advanced.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                stage: "Stage 1",
                title: "Fundamentals",
                skills: ["Line control", "Basic shapes", "Value scales", "Simple still life"]
              },
              {
                stage: "Stage 2",
                title: "Cast Drawing",
                skills: ["Sight-size method", "Form modeling", "Edge control", "Refined rendering"]
              },
              {
                stage: "Stage 3",
                title: "Figure Drawing",
                skills: ["Gesture", "Proportion", "Anatomy basics", "Value massing"]
              },
              {
                stage: "Stage 4",
                title: "Oil Painting",
                skills: ["Color theory", "Paint handling", "Composition", "Full paintings"]
              }
            ].map((stage, idx) => (
              <Card key={idx} className="border-stone-200">
                <CardContent className="p-6">
                  <span className="text-xs text-cafa-red font-medium">{stage.stage}</span>
                  <h3 className="text-xl font-semibold text-stone-900 mt-1 mb-4">{stage.title}</h3>
                  <ul className="space-y-2">
                    {stage.skills.map((skill, i) => (
                      <li key={i} className="text-stone-600 text-sm flex items-center">
                        <CheckCircle size={14} className="text-cafa-red mr-2 flex-shrink-0" />
                        {skill}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Techniques Taught */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4 text-center">
            Classical Techniques We Teach
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto text-center mb-12">
            Learn the methods used by master painters throughout history.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Sight-Size Drawing",
                desc: "A classical method where the drawing is made the same size as the subject appears from a fixed viewing point, allowing direct comparison for accuracy."
              },
              {
                title: "Bargue Plate Studies",
                desc: "Copying from the famous Bargue drawing course, a 19th-century method still used by leading ateliers worldwide to develop precision and sensitivity."
              },
              {
                title: "Cast Drawing",
                desc: "Drawing from plaster casts of classical sculpture, an essential step in understanding form, light, and shadow before working from the live model."
              },
              {
                title: "Grisaille Underpainting",
                desc: "Creating a monochromatic value study in oils before adding color, a technique used by many old masters to establish form and composition."
              },
              {
                title: "Alla Prima Painting",
                desc: "Direct painting completed in a single session, capturing fresh impressions and spontaneous brushwork while maintaining accurate observation."
              },
              {
                title: "Layered Painting",
                desc: "Building paintings in stages from lean to fat, allowing for rich color depth and luminosity in the tradition of the Flemish masters."
              }
            ].map((technique, idx) => (
              <Card key={idx} className="border-stone-200">
                <CardContent className="p-6">
                  <Palette className="text-cafa-red mb-3" size={28} />
                  <h3 className="text-lg font-semibold text-stone-900 mb-2">{technique.title}</h3>
                  <p className="text-stone-600 text-sm">{technique.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Classical Training */}
      <section className="py-16 lg:py-20 bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif mb-4">
              Why Classical Training Matters
            </h2>
            <p className="text-lg text-stone-400 max-w-2xl mx-auto">
              In an age of digital art and conceptual work, classical skills remain 
              relevant and valuable.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                title: "Transferable Skills",
                desc: "The observation and rendering skills developed through classical training transfer to any medium—digital, animation, illustration, or fine art."
              },
              {
                title: "Professional Foundation",
                desc: "Many successful contemporary artists, illustrators, and concept artists credit their classical training as the foundation of their careers."
              },
              {
                title: "Artistic Freedom",
                desc: "Mastering representational skills gives you the freedom to paint what you imagine. You're not limited by technical inability."
              },
              {
                title: "Timeless Beauty",
                desc: "Classical techniques allow you to create work with lasting beauty and craftsmanship that stands the test of time."
              }
            ].map((benefit, idx) => (
              <div key={idx} className="flex items-start">
                <Award className="text-cafa-gold mr-4 mt-1 flex-shrink-0" size={24} />
                <div>
                  <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-stone-400">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-8 text-center">
            Classical Training FAQs
          </h2>
          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <div key={idx} className="border-b border-stone-200 pb-6">
                <h3 className="text-lg font-semibold text-stone-900 mb-2">{faq.question}</h3>
                <p className="text-stone-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-cafa-red text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-serif mb-4">
            Begin Your Classical Art Training
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join San Antonio&apos;s atelier tradition and develop the skills of the masters.
          </p>
          <Button asChild size="lg" className="bg-white text-cafa-red hover:bg-stone-100">
            <Link to="/classes/adult">View Classes & Enroll</Link>
          </Button>
        </div>
      </section>
    </main>
  );
};

export default ClassicalArtTraining;
